import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, logActivity } from '../api/client';
import StageGuide from '../components/StageGuide';
import { markOnboardingStep } from '../lib/onboarding';

// CSV-aware row splitter (handles quoted fields with embedded newlines)
function splitCsvRows(text: string): string[] {
  const rows: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      if (current.trim()) rows.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) rows.push(current);
  return rows;
}

const CHUNK_SIZE = 40000; // ~40KB per chunk to stay under API Gateway limits with base64 encoding

export default function SamLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  // Allow deep-linking to a specific award/notice: /sam-library?search=<PIID>.
  // Reference-award links in the Price Benchmark open the record this way.
  const initialSearch = (() => {
    try { return new URLSearchParams(location.search).get('search') || ''; }
    catch { return ''; }
  })();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(initialSearch);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [setAsideFilter, setSetAsideFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [naicsFilter, setNaicsFilter] = useState('');
  const [pscFilter, setPscFilter] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [popFilter, setPopFilter] = useState('');
  const [awardeeFilter, setAwardeeFilter] = useState('');
  const [responseDateFrom, setResponseDateFrom] = useState('');
  const [responseDateTo, setResponseDateTo] = useState('');
  const [updatedDateFrom, setUpdatedDateFrom] = useState('');
  const [updatedDateTo, setUpdatedDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvProgress, setCsvProgress] = useState('');
  const [csvResult, setCsvResult] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<any>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [samFiles, setSamFiles] = useState<File[]>([]);
  const [samAiLoading, setSamAiLoading] = useState(false);
  const [samAiResult, setSamAiResult] = useState<any>(null);
  const [promoteResult, setPromoteResult] = useState<{ success?: string; error?: string } | null>(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 50;
  const csvFileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    // When deep-linked to a single award (?award=<id>), the dedicated effect
    // loads that record — don't let the empty-filter branch clear it.
    let hasAwardParam = false;
    try { hasAwardParam = !!new URLSearchParams(location.search).get('award'); } catch { /* noop */ }
    if (hasAwardParam && !search) return;
    if (!search && typeFilter.length === 0 && !setAsideFilter && !statusFilter && !naicsFilter && !pscFilter && !agencyFilter && !popFilter && !awardeeFilter && !responseDateFrom && !responseDateTo && !updatedDateFrom && !updatedDateTo) { 
      setItems([]); 
      // Fetch the real record count (cached server-side) instead of a hardcoded number.
      try {
        const c = await api.get<any>('/sam-library/count');
        setTotalItems(c.total || 0);
      } catch {
        setTotalItems(0);
      }
      setTotalPages(1);
      return; 
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      if (search) params.set('search', search);
      if (typeFilter.length > 0) params.set('notice_types', typeFilter.join(','));
      if (setAsideFilter) params.set('set_aside', setAsideFilter);
      if (naicsFilter) params.set('naics', naicsFilter);
      if (pscFilter) params.set('psc', pscFilter);
      if (agencyFilter) params.set('agency', agencyFilter);
      if (popFilter) params.set('pop', popFilter);
      if (awardeeFilter) params.set('awardee', awardeeFilter);
      if (responseDateFrom) params.set('response_date_from', responseDateFrom);
      if (responseDateTo) params.set('response_date_to', responseDateTo);
      if (updatedDateFrom) params.set('updated_date_from', updatedDateFrom);
      if (updatedDateTo) params.set('updated_date_to', updatedDateTo);
      const res = await api.get<any>(`/sam-library?${params.toString()}`);
      setItems(res.items || []);
      setTotalItems(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [page, search, typeFilter, setAsideFilter, statusFilter, naicsFilter, pscFilter, agencyFilter, popFilter, awardeeFilter, responseDateFrom, responseDateTo, updatedDateFrom, updatedDateTo, location.search]);

  useEffect(() => { load(); }, [load]);

  // Deep-link to a specific award record: /sam-library?award=<id>. Fetch that
  // one record by id and show it expanded (used by Price Benchmark reference
  // links). Runs once on mount.
  useEffect(() => {
    let awardId = '';
    try { awardId = new URLSearchParams(location.search).get('award') || ''; } catch { /* noop */ }
    if (!awardId) return;
    (async () => {
      setLoading(true); setError('');
      try {
        const detail = await api.get<any>(`/sam-library/${encodeURIComponent(awardId)}`);
        const rec = detail?.item || detail?.document || detail;
        if (rec && (rec.id || rec.notice_id)) {
          const withId = { ...rec, id: rec.id || awardId };
          setItems([withId]);
          setTotalItems(1);
          setTotalPages(1);
          setExpandedRow(withId.id);
          setExpandedDetail(withId);
        } else {
          setError('Award record not found.');
        }
      } catch (e: any) {
        setError(e?.message || 'Could not load the award record.');
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  // Upload a single CSV file (chunked). Returns its aggregated result counts.
  const uploadOneCsv = async (file: File, fileLabel: string) => {
    const text = await file.text();
    const allRows = splitCsvRows(text);
    if (allRows.length < 2) {
      return { rows_parsed: 0, rows_created: 0, rows_updated: 0, rows_skipped: 0, alerts_triggered: 0, empty: true };
    }
    const header = allRows[0];
    const dataRows = allRows.slice(1);

    // Chunk by byte size
    const chunks: string[] = [];
    let currentChunk = header + '\n';
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i] + '\n';
      if ((currentChunk.length + row.length) > CHUNK_SIZE && currentChunk !== header + '\n') {
        chunks.push(currentChunk);
        currentChunk = header + '\n' + row;
      } else {
        currentChunk += row;
      }
    }
    if (currentChunk.trim() && currentChunk !== header) chunks.push(currentChunk);

    const agg = { rows_parsed: 0, rows_created: 0, rows_updated: 0, rows_skipped: 0, alerts_triggered: 0 };
    for (let i = 0; i < chunks.length; i++) {
      setCsvProgress(`${fileLabel}: uploading chunk ${i + 1}/${chunks.length}...`);
      const encoded = btoa(unescape(encodeURIComponent(chunks[i])));
      const res = await api.post<any>('/sam-library/csv-upload', {
        csv_data: encoded,
        chunk_index: i,
        total_chunks: chunks.length,
        is_chunked: chunks.length > 1,
      });
      agg.rows_created += res.rows_created || 0;
      agg.rows_skipped += res.rows_skipped || 0;
      agg.rows_updated += res.rows_updated || 0;
      agg.rows_parsed += res.rows_parsed || 0;
      agg.alerts_triggered += res.alerts_triggered || 0;
    }
    return agg;
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setCsvUploading(true);
    setCsvResult(null);
    setError('');

    const totals = { rows_parsed: 0, rows_created: 0, rows_updated: 0, rows_skipped: 0, alerts_triggered: 0 };
    const perFile: { name: string; created: number; updated: number; skipped: number }[] = [];
    let fileErrors = 0;

    for (let f = 0; f < files.length; f++) {
      const file = files[f];
      const label = `File ${f + 1}/${files.length} (${file.name})`;
      setCsvProgress(`${label}: reading...`);
      try {
        const r = await uploadOneCsv(file, label);
        totals.rows_parsed += r.rows_parsed;
        totals.rows_created += r.rows_created;
        totals.rows_updated += r.rows_updated;
        totals.rows_skipped += r.rows_skipped;
        totals.alerts_triggered += r.alerts_triggered;
        perFile.push({ name: file.name, created: r.rows_created, updated: r.rows_updated, skipped: r.rows_skipped });
      } catch (err: any) {
        fileErrors++;
        perFile.push({ name: file.name, created: 0, updated: 0, skipped: -1 });
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }

    setCsvResult({ ...totals, files: files.length, per_file: perFile, file_errors: fileErrors });
    if (fileErrors > 0) setError(`${fileErrors} of ${files.length} file(s) failed to upload — see per-file results.`);
    setCsvProgress('');
    load();
    setCsvUploading(false);
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  const handleDelete = async (id: string) => {
    try {
      await api.del(`/sam-library/${id}`);
      setDeleteConfirm(null);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Delete ALL SAM.gov Library entries? This cannot be undone.')) return;
    try {
      setCsvProgress('Deleting all...');
      await api.del<any>('/sam-library/all');
      setCsvProgress('');
      load();
    } catch (e: any) {
      setError(e.message);
      setCsvProgress('');
    }
  };

  const handleExpand = async (id: string) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedRow(id);
    setExpandedDetail(null);
    setPromoteLoading(false);
    setPromoteResult(null);
    setExpandedLoading(true);
    try {
      const detail = await api.get<any>(`/sam-library/${id}`);
      setExpandedDetail(detail);
    } catch {
      // Fallback to list data
      setExpandedDetail(items.find(i => i.id === id) || null);
    }
    setExpandedLoading(false);
  };

  // Compute filter options from data
  const types = Array.from(new Set(items.map(i => i.notice_type).filter(Boolean))).sort();
  const setAsides = Array.from(new Set(items.map(i => i.set_aside).filter(Boolean))).sort();

  // Parse date helper
  const parseDate = (dateStr: string): number | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d.getTime();
  };

  // Client-side filtering
  let filtered = items;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(i =>
      (i.title || '').toLowerCase().includes(s) ||
      (i.notice_id || '').toLowerCase().includes(s) ||
      (i.description || '').toLowerCase().includes(s) ||
      (i.naics || '').toLowerCase().includes(s) ||
      (i.psc || '').toLowerCase().includes(s) ||
      (i.agency || '').toLowerCase().includes(s) ||
      (i.poc || '').toLowerCase().includes(s)
    );
  }
  if (typeFilter.length > 0) filtered = filtered.filter(i => typeFilter.includes(i.notice_type));
  if (setAsideFilter) filtered = filtered.filter(i => (i.set_aside || '').toLowerCase().includes(setAsideFilter.toLowerCase()));
  if (statusFilter) filtered = filtered.filter(i => i.active_status === statusFilter);
  if (naicsFilter) filtered = filtered.filter(i => (i.naics || '').startsWith(naicsFilter));
  if (pscFilter) filtered = filtered.filter(i => (i.psc || '').toLowerCase().startsWith(pscFilter.toLowerCase()));
  if (agencyFilter) filtered = filtered.filter(i => ((i.agency || '') + ' ' + (i.sub_agency || '')).toLowerCase().includes(agencyFilter.toLowerCase()));
  if (popFilter) filtered = filtered.filter(i => ((i.place_of_performance || '') + ' ' + (i.pop_state || '') + ' ' + (i.pop_city || '')).toLowerCase().includes(popFilter.toLowerCase()));
  if (awardeeFilter) filtered = filtered.filter(i => ((i.awardee || '') + ' ' + (i.awardee_name || '')).toLowerCase().includes(awardeeFilter.toLowerCase()));
  if (responseDateFrom) {
    const from = new Date(responseDateFrom).getTime();
    filtered = filtered.filter(i => { const d = parseDate(i.response_date); return d !== null && d >= from; });
  }
  if (responseDateTo) {
    const to = new Date(responseDateTo).getTime() + 86400000;
    filtered = filtered.filter(i => { const d = parseDate(i.response_date); return d !== null && d <= to; });
  }
  if (updatedDateFrom) {
    const from = new Date(updatedDateFrom).getTime();
    filtered = filtered.filter(i => { const d = parseDate(i.last_modified_date); return d !== null && d >= from; });
  }
  if (updatedDateTo) {
    const to = new Date(updatedDateTo).getTime() + 86400000;
    filtered = filtered.filter(i => { const d = parseDate(i.last_modified_date); return d !== null && d <= to; });
  }

  const NOTICE_TYPES = [
    'Sources Sought',
    'Presolicitation',
    'Solicitation',
    'Combined Synopsis/Solicitation',
    'Award Notice',
    'Justification',
  ];

  const handleTypeToggle = (type: string) => {
    setTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const clearAllFilters = () => {
    setSearch('');
    setTypeFilter([]);
    setSetAsideFilter('');
    setStatusFilter('');
    setNaicsFilter('');
    setPscFilter('');
    setAgencyFilter('');
    setPopFilter('');
    setAwardeeFilter('');
    setResponseDateFrom('');
    setResponseDateTo('');
    setUpdatedDateFrom('');
    setUpdatedDateTo('');
  };

  const hasActiveFilters = search || typeFilter.length > 0 || setAsideFilter || statusFilter || naicsFilter || pscFilter || agencyFilter || popFilter || awardeeFilter || responseDateFrom || responseDateTo || updatedDateFrom || updatedDateTo;

  // Client-side filtering still applies on the server-returned page

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, typeFilter, setAsideFilter, statusFilter, naicsFilter, pscFilter, agencyFilter, popFilter, awardeeFilter, responseDateFrom, responseDateTo, updatedDateFrom, updatedDateTo]);

  return (
    <div>
      <StageGuide id="sam-library" />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>SAM.gov Library</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {totalItems.toLocaleString()}+ federal procurement records. Search by keyword, NAICS, agency, or awardee.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="file"
            ref={csvFileRef}
            accept=".csv"
            multiple
            style={{ display: 'none' }}
            onChange={handleCsvUpload}
          />
          <button
            onClick={() => csvFileRef.current?.click()}
            disabled={csvUploading}
            style={{
              padding: '9px 18px',
              background: csvUploading ? '#94a3b8' : '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: csvUploading ? 'default' : 'pointer',
            }}
          >
            {csvUploading ? 'Uploading...' : 'Upload SAM.gov CSVs'}
          </button>

        </div>
      </div>

      {/* Upload progress / result */}
      {csvProgress && (
        <div role="status" aria-live="polite" style={{ padding: '10px 16px', background: '#eef2ff', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#3730a3' }}>
          {csvProgress}
        </div>
      )}
      {csvResult && (
        <div role="status" aria-live="polite" style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#166534' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {csvResult.files > 1 && <span><strong>{csvResult.files}</strong> files processed{csvResult.file_errors > 0 ? ` (${csvResult.file_errors} failed)` : ''} —</span>}
            <span>Parsed: <strong>{csvResult.rows_parsed}</strong></span>
            <span>New: <strong>{csvResult.rows_created}</strong></span>
            <span>Updated: <strong>{csvResult.rows_updated}</strong></span>
            <span>Skipped: <strong>{csvResult.rows_skipped}</strong></span>
            {csvResult.alerts_triggered > 0 && (
              <span style={{ color: '#b45309' }}>Alerts triggered: <strong>{csvResult.alerts_triggered}</strong></span>
            )}
            <button onClick={() => setCsvResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16 }}>dismiss</button>
          </div>
          {csvResult.per_file && csvResult.per_file.length > 1 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #bbf7d0', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {csvResult.per_file.map((pf: any, i: number) => (
                <div key={i} style={{ color: pf.skipped === -1 ? '#991b1b' : '#166534' }}>
                  {pf.skipped === -1 ? '✕' : '✓'} {pf.name}: {pf.skipped === -1 ? 'failed' : `${pf.created} new, ${pf.updated} updated, ${pf.skipped} skipped`}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={{ padding: '10px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#dc2626' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
        {/* Quick view toggle: All / Opportunities / Awards */}
        {(() => {
          const OPP_TYPES = ['Sources Sought', 'Presolicitation', 'Solicitation', 'Combined Synopsis/Solicitation', 'Justification'];
          const isAwards = typeFilter.length === 1 && typeFilter[0] === 'Award Notice';
          const isOpps = typeFilter.length === OPP_TYPES.length && OPP_TYPES.every(t => typeFilter.includes(t));
          const isAll = typeFilter.length === 0;
          const seg = (label: string, active: boolean, onClick: () => void) => (
            <button onClick={onClick} style={{
              padding: '7px 16px', border: '1px solid #d1d5db', background: active ? '#4f46e5' : 'white',
              color: active ? 'white' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{label}</button>
          );
          return (
            <div style={{ display: 'flex', marginBottom: 12, borderRadius: 8, overflow: 'hidden', width: 'fit-content', border: '1px solid #d1d5db' }}>
              {seg('All', isAll, () => setTypeFilter([]))}
              {seg('📋 Opportunities (RFI/RFQ)', isOpps, () => setTypeFilter([...OPP_TYPES]))}
              {seg('🏆 Awards', isAwards, () => setTypeFilter(['Award Notice']))}
            </div>
          );
        })()}
        {/* Top row: search + quick actions */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Search by keyword, Notice ID (e.g. W91QVN-17-R-008)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && search.trim()) { logActivity('search', { query: search.trim(), page: 'SAM Library' }); markOnboardingStep('search_sam'); } }}
            aria-label="Search by keyword or Notice ID"
            style={{ flex: 1, minWidth: 260, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
          />
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
            style={{
              padding: '10px 16px',
              background: showAdvanced ? '#eef2ff' : '#f8fafc',
              color: showAdvanced ? '#4338ca' : '#475569',
              border: `1px solid ${showAdvanced ? '#c7d2fe' : '#e2e8f0'}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showAdvanced ? 'Hide Filters' : 'Advanced Filters'}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{ padding: '10px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
            >
              Clear All
            </button>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Status filter" style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }}>
            <option value="">Active &amp; Inactive</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
          </select>
        </div>

        {/* Advanced filters panel */}
        {showAdvanced && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px 24px' }}>
            {/* Dates - Response Due */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Response/Date Offers Due</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={responseDateFrom} onChange={e => setResponseDateFrom(e.target.value)} aria-label="Response date from" style={dateInputStyle} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>to</span>
                <input type="date" value={responseDateTo} onChange={e => setResponseDateTo(e.target.value)} aria-label="Response date to" style={dateInputStyle} />
              </div>
            </div>

            {/* Dates - Updated */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Updated Date</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={updatedDateFrom} onChange={e => setUpdatedDateFrom(e.target.value)} aria-label="Updated date from" style={dateInputStyle} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>to</span>
                <input type="date" value={updatedDateTo} onChange={e => setUpdatedDateTo(e.target.value)} aria-label="Updated date to" style={dateInputStyle} />
              </div>
            </div>

            {/* Product/Service codes */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>NAICS Code</div>
              <input
                placeholder="Example: 621511"
                value={naicsFilter}
                onChange={e => setNaicsFilter(e.target.value)}
                aria-label="NAICS Code filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, marginTop: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Product and Service Code</div>
              <input
                placeholder="Example: C1LB"
                value={pscFilter}
                onChange={e => setPscFilter(e.target.value)}
                aria-label="Product and Service Code filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
            </div>

            {/* Notice Type checkboxes */}
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notice Type</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {NOTICE_TYPES.map(type => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={typeFilter.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                      style={{ accentColor: '#4f46e5' }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Set Aside */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Set Aside</div>
              <input
                placeholder="e.g. Small Business"
                value={setAsideFilter}
                onChange={e => setSetAsideFilter(e.target.value)}
                aria-label="Set Aside filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
            </div>

            {/* Federal Organization (agency) */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Federal Organization</div>
              <input
                placeholder="e.g. Homeland Security, Army, GSA"
                value={agencyFilter}
                onChange={e => setAgencyFilter(e.target.value)}
                aria-label="Federal Organization filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
            </div>

            {/* Place of Performance */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Place of Performance</div>
              <input
                placeholder="e.g. Virginia, Washington DC"
                value={popFilter}
                onChange={e => setPopFilter(e.target.value)}
                aria-label="Place of Performance filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
            </div>

            {/* Contract Awardee */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Contract Awardee</div>
              <input
                placeholder="e.g. Carahsoft, Leidos"
                value={awardeeFilter}
                onChange={e => setAwardeeFilter(e.target.value)}
                aria-label="Contract Awardee filter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
            </div>

            {/* Active filter count */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              {hasActiveFilters && (
                <div style={{ fontSize: 12, color: '#4338ca', fontWeight: 500 }}>
                  Showing {Math.min(filtered.length, PAGE_SIZE)} of {totalItems.toLocaleString()} records
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '10px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalItems)} of {totalItems} records
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={paginationBtnStyle(page === 1)} aria-label="First page">First</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={paginationBtnStyle(page === 1)} aria-label="Previous page">Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 12, color: '#374151', fontWeight: 600 }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={paginationBtnStyle(page === totalPages)} aria-label="Next page">Next</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={paginationBtnStyle(page === totalPages)} aria-label="Last page">Last</button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{totalItems.toLocaleString()}+ Records Ready to Search</div>
          <div style={{ fontSize: 13, maxWidth: 500, margin: '0 auto', lineHeight: 1.6 }}>
            This library contains federal procurement opportunities and award notices from SAM.gov.
            Use the search bar and filters above to find opportunities by keyword, NAICS code, type, or agency.
          </div>
        </div>
      ) : (
        <>
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={thStyle}>Notice ID</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>NAICS</th>
                <th style={thStyle}>PSC</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Response Date</th>
                <th style={thStyle}>Set Aside</th>
                <th style={thStyle}>Active</th>
                <th style={thStyle}>POC</th>
                <th style={{ ...thStyle, width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <React.Fragment key={item.id}>
                  <tr
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                    onClick={() => handleExpand(item.id)}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{item.notice_id || '—'}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 240 }}>
                      <div style={{ fontWeight: 500, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                        {item.title}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 160 }}>
                      <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                        {item.naics || '—'}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 180 }}>
                      <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                        {item.psc || '—'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '3px 8px', background: '#eef2ff', color: '#4338ca', borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap' }}>
                        {item.notice_type || '—'}
                      </span>
                    </td>
                    <td style={tdStyle}><span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>{item.response_date || '—'}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 150 }}>
                      <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                        {item.set_aside || '—'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 500,
                        background: item.active_status === 'Active' ? '#dcfce7' : '#fee2e2',
                        color: item.active_status === 'Active' ? '#166534' : '#991b1b',
                      }}>
                        {item.active_status || '—'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 130 }}>
                      <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>
                        {item.poc || '—'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {deleteConfirm === item.id ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} aria-label="Confirm delete" style={{ fontSize: 10, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>Yes</button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }} aria-label="Cancel delete" style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>No</button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item.id); }}
                          style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                          title="Delete"
                          aria-label="Delete entry"
                        >
                          🗑
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Expanded row detail */}
                  {expandedRow === item.id && (
                    <tr>
                      <td colSpan={10} style={{ padding: '16px 24px', background: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}>
                        {expandedLoading ? (
                          <div style={{ color: '#64748b', fontSize: 12 }}>Loading details...</div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>
                              {(expandedDetail || item).title}
                            </div>
                            {(() => {
                              // Award records (source fpds_awards_csv / notice_type
                              // "Award Notice") store their data under different field
                              // names than notices. Read both so nothing shows blank.
                              const d = (expandedDetail || item) as any;
                              const isAward = d.source === 'fpds_awards_csv' || d.notice_type === 'Award Notice'
                                || d.piid || d.awardee_name || d.dollars_obligated != null;
                              const awardee = d.awardee || d.awardee_name || '—';
                              const awardNo = d.award_number || d.piid || '—';
                              const awardDate = d.award_date || d.date_signed || '—';
                              const naics = d.naics || d.naics_code || '';
                              const naicsDesc = d.naics_description || '';
                              const psc = d.psc || d.psc_code || '';
                              const pscDesc = d.psc_description || '';
                              const dollars = d.dollars_obligated ?? d.dollars ?? d.total_contract_value;
                              const money = (v: any) => {
                                const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,]/g, ''));
                                return isNaN(n) ? '—' : '$' + Math.round(n).toLocaleString();
                              };
                              return (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 24px', fontSize: 12, marginBottom: 12 }}>
                                  <div><strong>Notice ID:</strong> {d.notice_id || '—'}</div>
                                  <div><strong>Agency:</strong> {d.agency || '—'}</div>
                                  <div><strong>{isAward ? 'Sub-Agency' : 'POC'}:</strong> {isAward ? (d.sub_agency || '—') : (d.poc || '—')}</div>
                                  <div><strong>Awardee:</strong> {awardee}</div>
                                  <div><strong>{isAward ? 'Award / PIID' : 'Award Number'}:</strong> {awardNo}</div>
                                  <div><strong>{isAward ? 'Date Signed' : 'Award Date'}:</strong> {awardDate}</div>
                                  {isAward && <div><strong>Obligated:</strong> {money(dollars)}</div>}
                                  {isAward && <div><strong>NAICS:</strong> {naics ? `${naics}${naicsDesc ? ' — ' + naicsDesc : ''}` : '—'}</div>}
                                  {isAward && <div><strong>PSC:</strong> {psc ? `${psc}${pscDesc ? ' — ' + pscDesc : ''}` : '—'}</div>}
                                  {isAward && d.type_of_contract_pricing && <div><strong>Pricing:</strong> {d.type_of_contract_pricing}</div>}
                                  {isAward && d.pop_state && <div><strong>Place of Performance:</strong> {d.pop_state}</div>}
                                  {isAward && d.extent_competed && <div><strong>Competition:</strong> {d.extent_competed}</div>}
                                </div>
                              );
                            })()}
                            <div>
                              <strong style={{ fontSize: 12 }}>Full Description:</strong>
                              <div style={{ marginTop: 6, padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 300, overflowY: 'auto', fontSize: 12, lineHeight: 1.7, color: '#374151', whiteSpace: 'pre-wrap' }}>
                                {(expandedDetail || item).description
                                  || (expandedDetail || item).transaction_description
                                  || (expandedDetail || item).psc_description
                                  || 'No description available.'}
                              </div>
                            </div>

                            {/* Upload & AI Analysis Section */}
                            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                              <h4 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>📎 Upload Notice Files for AI Analysis</h4>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <label style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  📄 Choose Files
                                  <input type="file" multiple accept=".pdf,.docx,.xlsx,.txt,.csv" onChange={e => { if (e.target.files) setSamFiles(Array.from(e.target.files)); }} style={{ display: 'none' }} />
                                </label>
                                {samFiles.length > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>{samFiles.length} file(s) selected</span>}
                                <button
                                  disabled={samFiles.length === 0 || samAiLoading}
                                  onClick={async () => {
                                    setSamAiLoading(true);
                                    setSamAiResult(null);
                                    try {
                                      // Upload files and get text extraction
                                      let allText = '';
                                      for (const file of samFiles) {
                                        const reader = new FileReader();
                                        const base64 = await new Promise<string>((res, rej) => {
                                          reader.onload = () => res((reader.result as string).split(',')[1]);
                                          reader.onerror = () => rej('Read error');
                                          reader.readAsDataURL(file);
                                        });
                                        // Upload to get text extraction
                                        try {
                                          const uploadRes = await api.post<any>('/documents', {
                                            opportunity_id: 'sam-analysis-temp',
                                            filename: file.name,
                                            file_content: base64,
                                            file_type: file.name.split('.').pop()?.toLowerCase() || 'pdf',
                                          });
                                          if (uploadRes.extracted_text) allText += '\n' + uploadRes.extracted_text;
                                        } catch { /* continue */ }
                                      }
                                      // Call AI analysis with the notice text + description
                                      const contextText = (expandedDetail || item).description || '';
                                      const analysisRes = await api.post<any>('/ai/ask', {
                                        question: `Analyze this federal procurement notice and extract structured data. Also identify the top 5-8 potential vendors from the Carahsoft/FTI vendor ecosystem that could fulfill this requirement based on their capabilities.

Return JSON with these fields: scope, mission_need, far_clauses, awardee, contract_type, pricing_type, estimated_value, period_of_performance, place_of_performance, deliverables, security_requirements, set_aside, evaluation_criteria, submission_requirements, key_dates, additional_notes, potential_vendors.

The potential_vendors field should be an array of objects with: name, fit_score (0-100), reason (one sentence explaining why they match).

Score vendors using FIT methodology:
- Mission Capability (30%): Does the vendor directly solve the core requirement?
- Domain Alignment (20%): Is this the vendor's primary federal domain?
- Deployment Reality (20%): Can they deploy in federal environments?
- Solution Completeness (15%): What % of requirements do they cover?
- Contract Fit (15%): Vehicle access, set-aside alignment, pricing model?

Document text: ${allText || contextText}`,
                                        context: 'sam_analysis',
                                      });
                                      const answer = analysisRes.answer || analysisRes.response || '';
                                      // Try to parse as JSON
                                      try {
                                        const parsed = JSON.parse(answer.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim());
                                        setSamAiResult(parsed);
                                      } catch {
                                        setSamAiResult({ additional_notes: answer });
                                      }
                                    } catch (err: any) {
                                      setSamAiResult({ error: err.message || 'Analysis failed' });
                                    }
                                    setSamAiLoading(false);
                                  }}
                                  style={{ padding: '8px 14px', background: samAiLoading ? '#93c5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: (samFiles.length === 0 || samAiLoading) ? 'not-allowed' : 'pointer', opacity: (samFiles.length === 0 || samAiLoading) ? 0.6 : 1 }}
                                >
                                  {samAiLoading ? '⏳ Analyzing...' : '✨ AI Analyze'}
                                </button>
                              </div>
                              <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Upload solicitation PDFs, attachments, or SOW documents. AI will extract scope, FAR clauses, pricing, deliverables, and more.</p>

                              {/* AI Results */}
                              {samAiResult && !samAiResult.error && (
                                <div style={{ marginTop: 12, padding: 14, background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8 }}>
                                  <h5 style={{ fontSize: 12, fontWeight: 600, color: '#5b21b6', marginBottom: 8 }}>AI Extracted Data</h5>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 12 }}>
                                    {samAiResult.scope && <div><strong>Scope:</strong> {String(samAiResult.scope).slice(0, 200)}</div>}
                                    {samAiResult.mission_need && <div><strong>Mission:</strong> {String(samAiResult.mission_need).slice(0, 200)}</div>}
                                    {samAiResult.contract_type && <div><strong>Contract Type:</strong> {samAiResult.contract_type}</div>}
                                    {samAiResult.pricing_type && <div><strong>Pricing:</strong> {samAiResult.pricing_type}</div>}
                                    {samAiResult.estimated_value && <div><strong>Value:</strong> {samAiResult.estimated_value}</div>}
                                    {samAiResult.awardee && <div><strong>Awardee:</strong> {samAiResult.awardee}</div>}
                                    {samAiResult.period_of_performance && <div><strong>PoP:</strong> {samAiResult.period_of_performance}</div>}
                                    {samAiResult.place_of_performance && <div><strong>Place:</strong> {samAiResult.place_of_performance}</div>}
                                    {samAiResult.set_aside && <div><strong>Set-Aside:</strong> {samAiResult.set_aside}</div>}
                                    {samAiResult.security_requirements && <div><strong>Security:</strong> {samAiResult.security_requirements}</div>}
                                  </div>
                                  {samAiResult.far_clauses && (
                                    <div style={{ marginTop: 8, fontSize: 11 }}><strong>FAR Clauses:</strong> {Array.isArray(samAiResult.far_clauses) ? samAiResult.far_clauses.join(', ') : samAiResult.far_clauses}</div>
                                  )}
                                  {samAiResult.deliverables && (
                                    <div style={{ marginTop: 4, fontSize: 11 }}><strong>Deliverables:</strong> {Array.isArray(samAiResult.deliverables) ? samAiResult.deliverables.join(', ') : samAiResult.deliverables}</div>
                                  )}
                                  {samAiResult.additional_notes && (
                                    <div style={{ marginTop: 8, fontSize: 11, color: '#4c1d95', whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto' }}><strong>Notes:</strong> {samAiResult.additional_notes}</div>
                                  )}
                                </div>
                              )}
                              {samAiResult?.error && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>Analysis failed: {samAiResult.error}</div>
                              )}

                              {/* Potential Vendors */}
                              {samAiResult && !samAiResult.error && samAiResult.potential_vendors && (
                                <div style={{ marginTop: 12, padding: 14, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8 }}>
                                  <h5 style={{ fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 8 }}>🎯 Potential Vendors (FIT Match)</h5>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {(samAiResult.potential_vendors || []).map((v: any, idx: number) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'white', borderRadius: 6, border: '1px solid #d1fae5' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#065f46', minWidth: 32 }}>{v.fit_score || v.score || '—'}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>{v.name || v.vendor}</span>
                                        <span style={{ fontSize: 11, color: '#6b7280', flex: 1 }}>{v.reason || v.rationale || ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Action Bar */}
                            <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb', alignItems: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => {
                                  // Build notes from AI results that don't have a direct field
                                  let notes = '';
                                  if (samAiResult && !samAiResult.error) {
                                    const notesParts: string[] = [];
                                    if (samAiResult.scope) notesParts.push(`SCOPE: ${samAiResult.scope}`);
                                    if (samAiResult.mission_need) notesParts.push(`MISSION: ${samAiResult.mission_need}`);
                                    if (samAiResult.far_clauses) notesParts.push(`FAR CLAUSES: ${Array.isArray(samAiResult.far_clauses) ? samAiResult.far_clauses.join(', ') : samAiResult.far_clauses}`);
                                    if (samAiResult.deliverables) notesParts.push(`DELIVERABLES: ${Array.isArray(samAiResult.deliverables) ? samAiResult.deliverables.join(', ') : samAiResult.deliverables}`);
                                    if (samAiResult.evaluation_criteria) notesParts.push(`EVALUATION: ${samAiResult.evaluation_criteria}`);
                                    if (samAiResult.submission_requirements) notesParts.push(`SUBMISSION: ${samAiResult.submission_requirements}`);
                                    if (samAiResult.key_dates) notesParts.push(`KEY DATES: ${samAiResult.key_dates}`);
                                    if (samAiResult.security_requirements) notesParts.push(`SECURITY: ${samAiResult.security_requirements}`);
                                    if (samAiResult.period_of_performance) notesParts.push(`PERIOD OF PERFORMANCE: ${samAiResult.period_of_performance}`);
                                    if (samAiResult.additional_notes) notesParts.push(`ADDITIONAL: ${samAiResult.additional_notes}`);
                                    notes = notesParts.join('\n\n');
                                  }

                                  const prefill = {
                                    title: item.title || '',
                                    agency: item.agency || '',
                                    notice_id: item.notice_id || '',
                                    naics_code: item.naics || '',
                                    psc_code: item.psc || '',
                                    set_aside: samAiResult?.set_aside || item.set_aside || '',
                                    notice_type: item.notice_type || '',
                                    response_deadline: item.response_date || '',
                                    publication_date: item.published_date || '',
                                    description: samAiResult?.scope || item.description || expandedDetail?.description || '',
                                    poc_primary_name: item.poc || '',
                                    budget: samAiResult?.estimated_value || item.award_amount || '',
                                    contract_type: samAiResult?.contract_type || '',
                                    place_of_performance: samAiResult?.place_of_performance || '',
                                    notes: notes,
                                  };
                                  navigate('/opportunities', { state: { prefill } });
                                }}
                                style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                              >
                                → Create Opportunity from this Notice
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination - moved below table removed, placed above */}
      </>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};

const dateInputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 12,
  width: '100%',
};

const paginationBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 500,
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  background: disabled ? '#f8fafc' : 'white',
  color: disabled ? '#cbd5e1' : '#374151',
  cursor: disabled ? 'default' : 'pointer',
});
